import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { PictureAsPdfRounded, RefreshRounded, SlideshowRounded } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { collection, getDocs } from 'firebase/firestore';
import type { AcademicAnalyticsResult } from '@dosfilos/application';
import { db } from '../../../../lib/firebase';
import { callable } from '../../../../lib/callable';
import { errorText } from '../../../../lib/error-text';
import { formatDateTime, formatNumber } from '../../../../lib/i18n-format';
import { useTenantStore } from '../../../../stores/tenant.store';
import { useTenantConfig } from '../../../../hooks/use-tenant-config';
import { graduationCandidatesApi } from '../../../../lib/api/graduation-candidates.api';
import { nearOptions } from '../reports/graduation-candidates.model';
import {
  countsModel,
  cohortModel,
  distributionModel,
  GAP_BUCKETS,
  seriesModel,
} from './analytics-charts.model';
import { SeriesChart } from './charts/SeriesChart';
import { GapLists, NearListDialog, RepeatList } from './NamedLists';
import type { GapBucket } from './analytics-charts.model';
import { useAnalyticsExport } from './pdf/use-analytics-export';
import { DistributionChart } from './charts/DistributionChart';
import { CohortChart } from './charts/CohortChart';
import { CandidatesChart } from './charts/CandidatesChart';
import { GRADE_COLORS, COHORT_COLORS } from './chart-palette';

// ─── /reportes → Panel académico (M1 · PR 1) ────────────────────────────────
// Los cuatro gráficos del boceto aprobado por el PM (2026-10-27): activos y
// persistencia, notas por curso, egreso por cohorte y candidatos. Todo llega
// calculado del servidor (`getAcademicAnalytics`, solo gestión), con la
// supresión ya aplicada: con n < mínimo, una tasa llega sin numerador y aquí se
// rotula «n < 10». Los conteos se muestran siempre. Sin nombres de personas.

/** Por defecto, los últimos 9 años; el selector llega hasta el inicio del análisis. */
export const DEFAULT_RANGE_YEARS = 9;

const getAcademicAnalytics = callable<
  {
    tenantId: string;
    filters: { fromYear: number; toYear: number; programId: string | null; site: string | null };
    refresh?: boolean;
  },
  AcademicAnalyticsResult
>('getAcademicAnalytics');

function ChartCard({
  title,
  definition,
  meta,
  children,
  foot,
}: {
  title: string;
  definition: string;
  meta: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, display: 'grid', gap: 1.5, minWidth: 0 }}
    >
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
        {definition}
      </Typography>
      <Stack direction="row" gap={1} flexWrap="wrap">
        {meta}
      </Stack>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
      {foot && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ maxWidth: '90ch' }}
        >
          {foot}
        </Typography>
      )}
    </Paper>
  );
}

function Swatch({ color }: { color: string }) {
  return (
    <Box
      component="span"
      sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: color, display: 'inline-block' }}
    />
  );
}

export function AnalyticsTab() {
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { currentTenantId } = useTenantStore();
  const tenantConfig = useTenantConfig();
  const queryClient = useQueryClient();
  const exporter = useAnalyticsExport();
  const thisYear = new Date().getFullYear();
  const [range, setRange] = useState<{ from: number; to: number } | null>(null);
  const [near, setNear] = useState<number | null>(null);
  /** La celda abierta de la tabla d (lista «cerca y sin inscripción reciente»). */
  const [cell, setCell] = useState<{ missing: number; gap: GapBucket } | null>(null);
  /** El diálogo del informe carta y su casilla «Incluir listas con nombres» (apagada). */
  const [letterOpen, setLetterOpen] = useState(false);
  const [includeNames, setIncludeNames] = useState(false);
  /** null = todos los programas / todas las sedes. */
  const [programId, setProgramId] = useState<string | null>(null);
  const [site, setSite] = useState<string | null>(null);
  const filters = {
    fromYear: range?.from ?? thisYear - DEFAULT_RANGE_YEARS + 1,
    toYear: range?.to ?? thisYear,
    programId,
    site,
  };
  const queryKey = ['academic-analytics', currentTenantId, filters];

  const analytics = useQuery({
    queryKey,
    enabled: !!currentTenantId,
    queryFn: async () => (await getAcademicAnalytics({ tenantId: currentTenantId!, filters })).data,
  });
  const programs = useQuery({
    queryKey: ['analytics-programs', currentTenantId],
    enabled: !!currentTenantId,
    queryFn: async () => {
      const snap = await getDocs(collection(db, 'tenants', currentTenantId!, 'programs'));
      return snap.docs
        .map((d) => ({ id: d.id, name: String(d.get('name') ?? d.id) }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
  const courses = useQuery({
    queryKey: ['analytics-courses', currentTenantId],
    enabled: !!currentTenantId,
    queryFn: async () => {
      const snap = await getDocs(collection(db, 'tenants', currentTenantId!, 'courses'));
      return new Map(
        snap.docs.map((d) => [
          d.id,
          { code: String(d.get('code') ?? d.id), name: String(d.get('name') ?? '') },
        ]),
      );
    },
  });
  // Candidatos existe solo con el expediente por credenciales (como la pestaña
  // Candidatos): sin él, el gráfico d lo dice en vez de pedir y fallar.
  const credentialRecord = tenantConfig.academicRecord.mode === 'credentials';
  // Al cargar, SOLO CIFRAS (sin nombres, ids ni códigos). Los nombres se piden
  // recién al abrir una celda (NearListDialog) o el anexo del informe carta.
  const candidates = useQuery({
    queryKey: ['graduation-candidates-counts', currentTenantId, programId, site],
    enabled: !!currentTenantId && credentialRecord,
    retry: false,
    queryFn: () => graduationCandidatesApi.counts({ tenantId: currentTenantId!, programId, site }),
  });

  const recompute = async () => {
    const res = await getAcademicAnalytics({ tenantId: currentTenantId!, filters, refresh: true });
    queryClient.setQueryData(queryKey, res.data);
  };

  const r = analytics.data;
  const startYear = r?.params.analysisStartYear ?? thisYear - DEFAULT_RANGE_YEARS + 1;
  const from = range?.from ?? Math.max(startYear, thisYear - DEFAULT_RANGE_YEARS + 1);
  const to = range?.to ?? thisYear;
  const years = useMemo(() => {
    const out: number[] = [];
    for (let y = startYear; y <= thisYear; y++) out.push(y);
    return out;
  }, [startYear, thisYear]);

  if (analytics.isError) return <Alert severity="error">{errorText(analytics.error)}</Alert>;
  if (!r) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const minN = r.params.minN;
  const snapDate = formatDateTime(r.dataAsOf, { dateStyle: 'medium', timeStyle: 'short' });
  const snapChip = (
    <Chip
      size="small"
      variant="outlined"
      label={t('reports.analytics.snapshot', { date: snapDate })}
    />
  );
  const rateName = t(`reports.analytics.distribution.rateName.${r.alertRateKind}`);
  const series = seriesModel(r);
  const dist = distributionModel(r, courses.data ?? new Map());
  // El egreso es por programa: el elegido, o el primero con plazo normal configurado.
  const cohortProgram =
    programId && r.graduation.cohorts[programId]
      ? programId
      : (Object.keys(r.graduation.cohorts)[0] ?? null);
  const cohorts = cohortProgram ? cohortModel(r, cohortProgram) : null;
  const nMinMax = series.points.length
    ? {
        min: Math.min(...series.points.map((p) => p.active)),
        max: Math.max(...series.points.map((p) => p.active)),
      }
    : { min: 0, max: 0 };
  const serverNear = candidates.data?.nearMaxCourses ?? tenantConfig.nearGraduationMaxCourses;
  const nearSel = near ?? serverNear;
  const cand = candidates.data ? countsModel(candidates.data, nearSel) : null;
  const exportPdf = (kind: 'slides' | 'letter', opts: { includeNames?: boolean } = {}) =>
    exporter.download(
      kind,
      {
        result: r,
        courses: courses.data ?? new Map(),
        data: {
          series,
          distribution: dist,
          cohorts,
          candidates: cand,
          dCountsInAlert: r.params.dCountsInAlertRate,
        },
        range: { from, to },
        programName: programId
          ? (programs.data?.find((p) => p.id === programId)?.name ?? programId)
          : null,
        site,
        near: nearSel,
        startYear,
      },
      opts,
    );
  const thresholdPct = formatNumber(r.params.alertRateThreshold, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (
    <Stack gap={2.5}>
      {/* Un solo control de años para a y b (PM). */}
      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" fontWeight={600}>
          {t('reports.analytics.range.label')}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel id="an-from">{t('reports.analytics.range.from')}</InputLabel>
          <Select
            labelId="an-from"
            label={t('reports.analytics.range.from')}
            value={from}
            onChange={(e) =>
              setRange({ from: Number(e.target.value), to: Math.max(Number(e.target.value), to) })
            }
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel id="an-to">{t('reports.analytics.range.to')}</InputLabel>
          <Select
            labelId="an-to"
            label={t('reports.analytics.range.to')}
            value={to}
            onChange={(e) =>
              setRange({ from: Math.min(from, Number(e.target.value)), to: Number(e.target.value) })
            }
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="an-program">{t('reports.analytics.filters.program')}</InputLabel>
          <Select
            labelId="an-program"
            label={t('reports.analytics.filters.program')}
            value={programId ?? '__all__'}
            onChange={(e) => setProgramId(e.target.value === '__all__' ? null : e.target.value)}
          >
            <MenuItem value="__all__">{t('reports.analytics.filters.allPrograms')}</MenuItem>
            {(programs.data ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="an-site">{t('reports.analytics.filters.site')}</InputLabel>
          <Select
            labelId="an-site"
            label={t('reports.analytics.filters.site')}
            value={site ?? '__all__'}
            onChange={(e) => setSite(e.target.value === '__all__' ? null : e.target.value)}
          >
            <MenuItem value="__all__">{t('reports.analytics.filters.allSites')}</MenuItem>
            {(r?.sites ?? []).map((x) => (
              <MenuItem key={x} value={x}>
                {x}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
          {t('reports.analytics.range.hint')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SlideshowRounded />}
          onClick={() => void exportPdf('slides')}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {t('reports.export.slides')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfRounded />}
          onClick={() => {
            setIncludeNames(false);
            setLetterOpen(true);
          }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {t('reports.export.letter')}
        </Button>
        <Tooltip title={t('reports.analytics.recompute')}>
          <IconButton
            size="small"
            onClick={() => void recompute()}
            aria-label={t('reports.analytics.recompute')}
          >
            <RefreshRounded />
          </IconButton>
        </Tooltip>
      </Stack>
      {exporter.error && <Alert severity="error">{exporter.error}</Alert>}
      <Dialog open={letterOpen} onClose={() => setLetterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('reports.analytics.export.letterDialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              {t('reports.analytics.export.letterDialog.desc')}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  id="analytics-letter-names"
                  checked={includeNames}
                  onChange={(e) => setIncludeNames(e.target.checked)}
                />
              }
              label={t('reports.analytics.export.letterDialog.includeNames')}
            />
            {includeNames && (
              <Alert severity="warning">{t('reports.analytics.export.letterDialog.warn')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLetterOpen(false)}>
            {t('reports.candidates.slides.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setLetterOpen(false);
              void exportPdf('letter', { includeNames });
            }}
          >
            {t('reports.candidates.slides.generate')}
          </Button>
        </DialogActions>
      </Dialog>
      <Typography variant="caption" color="text.secondary">
        {t('reports.analytics.snapshotHint')}
      </Typography>

      {/* a) Activos y persistencia */}
      <ChartCard
        title={t('reports.analytics.series.title')}
        definition={t('reports.analytics.series.definition')}
        meta={
          <>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={t('reports.analytics.series.n', nMinMax)}
            />
            {snapChip}
          </>
        }
        foot={
          <>
            {series.before !== null && (
              <div>
                {t('reports.analytics.series.before', { year: startYear, count: series.before })}
              </div>
            )}
            <div>{t('reports.analytics.series.foot')}</div>
          </>
        }
      >
        <SeriesChart model={series} minN={minN} />
      </ChartCard>

      {/* b) Notas por curso */}
      <ChartCard
        title={t('reports.analytics.distribution.title')}
        definition={t('reports.analytics.distribution.definition', { rate: rateName })}
        meta={
          <>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={t('reports.analytics.distribution.n')}
            />
            {snapChip}
            <Chip
              size="small"
              variant="outlined"
              label={t('reports.analytics.distribution.order')}
            />
          </>
        }
        foot={t('reports.analytics.distribution.foot', { rate: rateName, threshold: thresholdPct })}
      >
        <Stack
          direction="row"
          gap={2}
          flexWrap="wrap"
          sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}
        >
          <b>{t('reports.analytics.distribution.pass')}</b>
          {(['A', 'B', 'C', 'D', 'other'] as const)
            .filter((s) => !(s === 'D' && r.params.dCountsInAlertRate))
            .map((s) => (
              <Stack key={s} direction="row" gap={0.75} alignItems="center">
                <Swatch color={GRADE_COLORS[mode][s]!.fill} />
                {t(`reports.analytics.distribution.segment.${s}`)}
              </Stack>
            ))}
          <b>{rateName}</b>
          {(r.params.dCountsInAlertRate ? (['D', 'F', 'W'] as const) : (['F', 'W'] as const)).map(
            (s) => (
              <Stack key={s} direction="row" gap={0.75} alignItems="center">
                <Swatch color={GRADE_COLORS[mode][s]!.fill} />
                {t(`reports.analytics.distribution.segment.${s}`)}
              </Stack>
            ),
          )}
          <span>
            ▲{' '}
            {t('reports.analytics.distribution.flag', { rate: rateName, threshold: thresholdPct })}
          </span>
        </Stack>
        {dist.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('reports.analytics.empty')}
          </Typography>
        ) : (
          <DistributionChart
            rows={dist}
            minN={minN}
            rateName={rateName}
            dCountsInAlert={r.params.dCountsInAlertRate}
          />
        )}
        <RepeatList r={r} courses={courses.data ?? new Map()} />
      </ChartCard>

      {/* c) Egreso por cohorte */}
      <ChartCard
        title={t('reports.analytics.cohorts.title')}
        definition={t('reports.analytics.cohorts.definition')}
        meta={
          <>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={t('reports.analytics.cohorts.n')}
            />
            {snapChip}
          </>
        }
        foot={
          cohorts
            ? t('reports.analytics.cohorts.foot', {
                horizon: cohorts.horizon,
                late: cohorts.graduatedAfterHorizon,
                none: cohorts.withoutCourses,
              })
            : undefined
        }
      >
        {!cohorts ? (
          <Alert severity="info">{t('reports.analytics.cohorts.notConfigured')}</Alert>
        ) : (
          <>
            <Stack
              direction="row"
              gap={2}
              flexWrap="wrap"
              sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}
            >
              {cohorts.lines.map((l) => {
                const name =
                  l.key === 'before'
                    ? t('reports.analytics.cohorts.before', { year: startYear })
                    : `${l.fromYear}–${l.toYear}`;
                const status = !l.points
                  ? t('reports.analytics.suppressedLong', { min: minN })
                  : l.complete
                    ? t('reports.analytics.cohorts.complete')
                    : t('reports.analytics.cohorts.incomplete', { k: l.observedTerms });
                return (
                  <Stack key={l.key} direction="row" gap={0.75} alignItems="center">
                    {l.points ? (
                      <Swatch color={COHORT_COLORS[mode][l.slot % 8]!} />
                    ) : (
                      <Box sx={{ width: 12 }} />
                    )}
                    <b>{name}</b>
                    <span>{`${t('reports.analytics.n', { count: l.size })} · ${status}`}</span>
                  </Stack>
                );
              })}
            </Stack>
            <CohortChart model={cohorts} startYear={startYear} />
          </>
        )}
      </ChartCard>

      {/* d) Candidatos */}
      <ChartCard
        title={t('reports.analytics.candidates.title')}
        definition={t('reports.analytics.candidates.definition', { count: nearSel })}
        meta={
          <>
            {cand && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={t('reports.analytics.candidates.n', { count: cand.total })}
              />
            )}
            {snapChip}
          </>
        }
        foot={
          cand ? (
            <>
              <div>{t('reports.analytics.candidates.foot')}</div>
              {cand.withoutGap > 0 && (
                <div>
                  {t('reports.analytics.candidates.withoutGap', { count: cand.withoutGap })}
                </div>
              )}
            </>
          ) : undefined
        }
      >
        {!credentialRecord || candidates.isError ? (
          <Alert severity="info">{t('reports.analytics.candidates.unavailable')}</Alert>
        ) : !cand ? (
          <CircularProgress size={24} />
        ) : (
          <Stack gap={2}>
            <Stack direction="row" gap={1.5} alignItems="center">
              <Typography variant="body2">{t('reports.analytics.candidates.near')}</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={nearSel}
                onChange={(_, v: number | null) => v !== null && setNear(v)}
              >
                {nearOptions(serverNear).map((n) => (
                  <ToggleButton key={n} value={n} sx={{ minWidth: 40 }}>
                    {n}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
            <CandidatesChart model={cand} />
            <Box sx={{ overflowX: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('reports.analytics.candidates.cross')}
              </Typography>
              <Box
                component="table"
                sx={{
                  borderCollapse: 'collapse',
                  fontSize: 13,
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: '100%',
                }}
              >
                <thead>
                  <tr>
                    <Box
                      component="th"
                      sx={{ textAlign: 'left', p: 0.75, color: 'text.secondary' }}
                    >
                      {t('reports.analytics.candidates.x')}
                    </Box>
                    {GAP_BUCKETS.map((g) => (
                      <Box
                        component="th"
                        key={g}
                        sx={{ textAlign: 'right', p: 0.75, color: 'text.secondary' }}
                      >
                        {t(`reports.analytics.candidates.gaps.${g}`)}
                      </Box>
                    ))}
                    <Box
                      component="th"
                      sx={{ textAlign: 'right', p: 0.75, color: 'text.secondary' }}
                    >
                      {t('reports.analytics.candidates.total')}
                    </Box>
                  </tr>
                </thead>
                <tbody>
                  {cand.rows.map((row) => (
                    <tr key={row.missing}>
                      <Box
                        component="td"
                        sx={{ p: 0.75, fontWeight: 600, borderTop: 1, borderColor: 'divider' }}
                      >
                        {row.missing === 0
                          ? t('reports.analytics.candidates.zero')
                          : t('reports.analytics.candidates.row', { count: row.missing })}
                      </Box>
                      {GAP_BUCKETS.map((g) => (
                        <Box
                          component="td"
                          key={g}
                          sx={{ p: 0.75, textAlign: 'right', borderTop: 1, borderColor: 'divider' }}
                        >
                          {row.byGap[g] > 0 ? (
                            <Link
                              component="button"
                              type="button"
                              onClick={() => setCell({ missing: row.missing, gap: g })}
                              aria-label={t('reports.analytics.lists.near.open', {
                                count: row.byGap[g],
                                gap: t(`reports.analytics.candidates.gaps.${g}`),
                              })}
                            >
                              {row.byGap[g]}
                            </Link>
                          ) : (
                            row.byGap[g]
                          )}
                        </Box>
                      ))}
                      <Box
                        component="td"
                        sx={{
                          p: 0.75,
                          textAlign: 'right',
                          fontWeight: 600,
                          borderTop: 1,
                          borderColor: 'divider',
                        }}
                      >
                        {row.total}
                      </Box>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            <NearListDialog cell={cell} onClose={() => setCell(null)} r={r} near={nearSel} />
          </Stack>
        )}
        <GapLists r={r} />
      </ChartCard>
    </Stack>
  );
}
